import Script from 'next/script'
import React, { ReactElement } from 'react'
import { loadScriptOnce } from '../../utils'
import { isShortCode } from '../Parser/Parser'
import { ZoomIn } from '../ZoomIn'
import {
  SpecialBlockquote,
  StyledBlockquote,
  StyledFigure,
  StyledFigureWrapper,
  StyledH3,
  StyledMoreArticles,
  StyledOL,
  StyledP,
  StyledUL,
} from './ContentParser.style'

import dynamic from 'next/dynamic'
import { PWithADWrap } from './PWithADWrap'
import { IframeAttributes, IParseHTML } from './types'
import {
  cleanOutEndingEmptyLines,
  embedRegex,
  getWidthAndHeightFromEmbedTag,
} from './utils'

import * as cheerio from 'cheerio'

const ContentIframe = dynamic(() => import('./ContentIframe'), { ssr: false })
const SocialEmbed = dynamic(() => import('./SocialEmbed'))

export const parseHTML = ({
  html,
  adMidArticle = true,
  keyPrefix = '',
  addArticleBox = true,
  isArticleVod,
  isArticle,
  isFaceLift = false,
  bbProps,
}: IParseHTML) => {
  if (!html) {
    return []
  }
  const counts = { p: 0, char: 0, ad: 0, word: 0 }
  // index 0 in the gallery array is the article's main image,
  // so figures inside the content start at 1
  let figureCounter = 1

  let foundFirstImage = false,
    hasMoreArticles = false,
    moreArticlesHead: ReactElement | null = null

  const childNodes = cleanOutEndingEmptyLines(html)

  const nodesCount = childNodes.length - 1
  const $ = cheerio.load('')
  return childNodes
    .map((t, index) => {
      const node = $(t)
      const key = keyPrefix + '_cp_el_' + index
      const shortcodeElement = isShortCode(node.html() || '', key, bbProps)
      if (shortcodeElement) {
        return shortcodeElement
      }

      const script = node.find('script').get(0)
      const iframe = node.find('iframe').get(0)
      const tagName = t?.type === 'tag' ? t.name.toLowerCase() : false
      if (!tagName) {
        return null
      }
      switch (tagName) {
        case 'p':
          hasMoreArticles = false
          if ((node.html() || '').match(embedRegex)) {
            const urlMatch = (node.html() || '').match(embedRegex)
            if (urlMatch) {
              const url = urlMatch[1]
              const dimension = getWidthAndHeightFromEmbedTag(node.html() || '')
              return <SocialEmbed url={url} dimension={dimension} />
            }
          }
          if (script) {
            try {
              if (script.attribs.src) {
                const url = new URL(script.attribs.src)
                url && loadScriptOnce({ src: url.href, id: url.href })
                return null
              } else {
                return (
                  <Script
                    dangerouslySetInnerHTML={{
                      __html: $(script).html() || '',
                    }}
                    key={Date.now()}
                  />
                )
              }
            } catch (e) {
              console.log(
                '%cContentParser ',
                'background-color:darkred;color:white;padding:2px 5px;',
                e
              )
            }
          }
          if (iframe) {
            const iframeAttrs: IframeAttributes = {
              id: $(iframe).attr('id') || '',
              src: $(iframe).attr('src') || '',
              title: $(iframe).attr('title'),
              height: $(iframe).attr('height'),
              width: $(iframe).attr('width'),
              frameBorder: $(iframe).attr('frameborder'),
              style: $(iframe).attr('style'),
            }
            return <ContentIframe iframeAttrs={iframeAttrs} key={key} />
          }
          if ([' ', '&nbsp;', ''].includes(node.text().toLowerCase())) {
            return null
          }

          // debugger
          if (node.text().length > 0) {
            counts.p++
          }
          counts.char += node.text().length
          counts.word += node.text().split(' ').length

          if (addArticleBox && node.text().length > 0 && nodesCount > index) {
            switch (counts.ad) {
              case 0:
                if (counts.p >= 1 && counts.word >= 60) {
                  counts.ad++
                  counts.p = 0
                  counts.char = 0
                  counts.word = 0
                  return (
                    <PWithADWrap
                      key={key}
                      for_key={key}
                      html={node.html() || ''}
                      cls={node.attr('class') || ''}
                      adId={`In_article_box_${counts.ad}`}
                      isArticleVod={!!isArticleVod}
                      addType={'taboola'}
                      pos={counts.ad}
                    />
                  )
                }
                break
              case 1:
              case 2:
              case 3:
                if (counts.p >= 2 && counts.word >= 120) {
                  counts.ad++
                  counts.p = 0
                  counts.char = 0
                  counts.word = 0
                  return (
                    <PWithADWrap
                      key={key}
                      for_key={key}
                      html={node.html() || ''}
                      cls={node.attr('class') || ''}
                      adId={'In_article_box_' + counts.ad}
                      isArticleVod={!!isArticleVod}
                      pos={counts.ad}
                    />
                  )
                }
                break
              case 4:
                if (counts.p >= 2 && counts.word >= 120) {
                  counts.ad++
                  counts.p = 0
                  counts.char = 0
                  counts.word = 0
                  return (
                    <PWithADWrap
                      key={key}
                      for_key={key}
                      html={node.html() || ''}
                      cls={node.attr('class') || ''}
                      adId={'In_article_box_' + counts.ad}
                      isArticleVod={!!isArticleVod}
                      mobileOnly={true}
                      pos={counts.ad}
                    />
                  )
                }
            }
          }
          return (
            <StyledP
              key={key}
              dangerouslySetInnerHTML={{ __html: node.html() || '' }}
              className={node.attr('class') || ''}
              theme={{ isFaceLift }}
            />
          )
        case 'figure': {
          hasMoreArticles = false
          const imgSrc = node.find('img').attr('src')
          const key = imgSrc || Date.now()
          const html = (node.html() || '').replace(
            /\/image\/upload\//g,
            '/image/upload/t_image_article_800/'
          )
          // if (!foundFirstImage && adMidArticle && index < nodesCount) {
          //   foundFirstImage = true
          //
          //   return (
          //       <div key={key} style={{ position: 'relative' }}>
          //         <StyledFigure
          //             dangerouslySetInnerHTML={{ __html: html }}
          //             className={`${node.attr('class') || ''} article-image`}
          //             // TODO: Move style to styles file somehow
          //             style={{ marginBottom: '10px' }}
          //         />
          //         {isArticle && <ZoomIn index={1} key={key} />}
          //         {/*{midArticleTaboola}*/}
          //       </div>
          //   )
          // }
          return (
            <StyledFigureWrapper key={key}>
              <StyledFigure
                dangerouslySetInnerHTML={{ __html: html }}
                className={`${node.attr('class') || ''} article-image`}
              />
              {isArticle && (
                <ZoomIn index={figureCounter++} src={imgSrc} key={key} />
              )}
            </StyledFigureWrapper>
          )
        }
        case 'blockquote':
          hasMoreArticles = false
          if (
            node.attr('class') ||
            ''.includes('instagram-media') ||
            node.attr('class') ||
            ''.includes('tiktok-embed') ||
            node.attr('class') ||
            ''.includes('reddit-embed-bq') ||
            node.attr('class') ||
            ''.includes('facebook') ||
            node.attr('class') ||
            ''.includes('twitter-tweet')
          ) {
            return (
              <SpecialBlockquote
                key={key}
                dangerouslySetInnerHTML={{ __html: node.html() || '' }}
                className={node.attr('class') || ''}
              />
            )
          }
          return (
            <StyledBlockquote
              key={key}
              dangerouslySetInnerHTML={{ __html: node.html() || '' }}
              className={node.attr('class') || ''}
            />
          )
        case 'h3':
          moreArticlesHead = (
            <StyledH3
              dangerouslySetInnerHTML={{ __html: node.html() || '' }}
              key={key}
            />
          )
          hasMoreArticles = false
          if (node.text().includes('כתבות נוספות')) {
            hasMoreArticles = true
            return null
          }
          return moreArticlesHead
        case 'ul':
          if (hasMoreArticles) {
            hasMoreArticles = false
            if (!foundFirstImage && adMidArticle && index < nodesCount) {
              foundFirstImage = true
              return (
                <React.Fragment key={key}>
                  <StyledMoreArticles theme={{ isFaceLift }}>
                    {moreArticlesHead}
                    <StyledUL
                      dangerouslySetInnerHTML={{ __html: node.html() || '' }}
                    />
                  </StyledMoreArticles>
                  {/*{midArticleTaboola}*/}
                </React.Fragment>
              )
            }
            return (
              <StyledMoreArticles key={key}>
                {moreArticlesHead}
                <StyledUL
                  dangerouslySetInnerHTML={{ __html: node.html() || '' }}
                />
              </StyledMoreArticles>
            )
          }
          moreArticlesHead = null
          hasMoreArticles = false
          return (
            <StyledUL
              dangerouslySetInnerHTML={{ __html: node.html() || '' }}
              key={key}
            />
          )
        case 'ol':
          return (
            <StyledOL
              key={key}
              dangerouslySetInnerHTML={{ __html: node.html() || '' }}
            />
          )
        default:
          return (
            <div
              key={key}
              dangerouslySetInnerHTML={{ __html: node.toString() || '' }}
            />
          )
      }
    })
    .filter(el => el !== null) as JSX.Element[]
}
